import type React from 'react';
import type { PropsWithChildren } from 'react';

import { Accordion } from '@base-ui/react/accordion';
import { ChevronDownIcon } from '@vlabs/icons';
import type { WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './Accordion.module.sass';

const displayName = 'Accordion';
const testid = buildTestid(displayName);

interface AccordionRootProps extends PropsWithChildren, WithDataTestid {
  /** Значения развёрнутых по умолчанию блоков. */
  defaultValue?: string[];
  className?: string;
}

export function AccordionRoot({
  defaultValue = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
  children,
}: AccordionRootProps): React.JSX.Element {
  return (
    <Accordion.Root
      className={classNames(st.Root, className)}
      data-testid={dataTestId ?? testid('root')['data-testid']}
      defaultValue={defaultValue}
      multiple
    >
      {children}
    </Accordion.Root>
  );
}

interface AccordionItemProps extends PropsWithChildren, WithDataTestid {
  value: string;
}

export function AccordionItem({
  value,
  'data-testid': dataTestId = undefined,
  children,
}: AccordionItemProps): React.JSX.Element {
  return (
    <Accordion.Item
      className={st.Item}
      data-testid={dataTestId ?? testid(`item-${value}`)['data-testid']}
      value={value}
    >
      {children}
    </Accordion.Item>
  );
}

export function AccordionHeader({ children }: PropsWithChildren): React.JSX.Element {
  return <Accordion.Header className={st.Header}>{children}</Accordion.Header>;
}

export function AccordionTrigger({ children }: PropsWithChildren): React.JSX.Element {
  return (
    <Accordion.Trigger className={st.Trigger}>
      {children}
      <ChevronDownIcon className={st.Chevron} />
    </Accordion.Trigger>
  );
}

export function AccordionPanel({ children }: PropsWithChildren): React.JSX.Element {
  return <Accordion.Panel className={st.Panel}>{children}</Accordion.Panel>;
}
